import React, { useMemo, VFC } from 'react';
import { hesselViewModels } from '../../../lib/view-models';
import { createLeasemanagerOptions, formatPrice } from '../../../utils/helpers';
import {
    CalculatorSection,
    SliderContent,
    Label,
    LabelAndValue,
    ErrorText,
    CalculationsContainer,
    StyledAddOnsSection,
    StyledAddOnsHeader,
    StyledAddOnsOptionsGrid,
    StyledAddOnOption,
    StyledAddOnOptionLink,
    StyledAddOnOptionPrice,
    StyledCheckbox,
} from './lease-manager-calculations.styled';
import { ProductDetailsStore } from '../../../lib/state/hessel-site/product-details';
import { usePriceByOwnership } from '../hooks/use-price-by-ownership';
import { ProductDetailsPage as UmbracoProductDetailsPage } from '../../../lib/api/models/umbraco';
import { SteppedOptionsSlider } from '../../shared/stepped-options-slider/stepped-options-slider.component';
import { VehiclePurchaseType } from '../../../lib/api/models/hessel-api/vehicle';
import { EquipmentOwnership, LeaseManagerDefaultValues } from '../../../lib/view-models/vehicle';
import { Abbreviations } from '../../../constants/units-and-abbreviations';
import { formatDistance } from '../../../utils/helpers/distance.helper';
import { Button } from '../../shared';
import { EquipmentDialogManagerStore } from '../../../lib/state/hessel-site/equipment-dialog-manager';

type LeaseManagerCalculationsProps = {
    product: hesselViewModels.ProductDetails;
    page: UmbracoProductDetailsPage;
    ownership: hesselViewModels.OwnershipTab;
    defaultLeaseManagerSetupValues?: LeaseManagerDefaultValues | undefined;
    optionalAddOns?: EquipmentOwnership;
};

const residualValuePercentage = 5;
const residualValuePlusMinusCount = 2;
const downPaymentPercentage = 5;
const downPaymentPlusMinusCount = 2;

export const LeaseManagerCalculations: VFC<LeaseManagerCalculationsProps> = ({
    product,
    page,
    ownership,
    defaultLeaseManagerSetupValues,
    optionalAddOns,
}) => {
    const {
        setLeaseManagerFinancialDurationMonths,
        setLeaseManagerFinancialDownPayment,
        setLeaseManagerFinancialKilometersPerYear,
        setLeaseManagerFinancialResidualValue,
        updateOptionalEquipment,
    } = ProductDetailsStore.useStoreActions((actions) => actions);

    // Equipment Dialog Manager Store
    const { setSelectedOptionalEquipment } = EquipmentDialogManagerStore.useStoreActions((actions) => actions);

    const type: VehiclePurchaseType['financialLeasing'] | VehiclePurchaseType['operationalLeasing'] | VehiclePurchaseType['hireSelected'] =
        useMemo(() => {
            switch (ownership) {
                case 'Finansiel Leasing':
                    return product.purchaseTypes.financialLeasing;

                case 'Operationel Leasing':
                    return product.purchaseTypes.operationalLeasing;

                case 'HiRE Selected':
                    return product.purchaseTypes.hireSelected;

                default:
                    return product.purchaseTypes.financialLeasing;
            }
        }, [ownership, product.purchaseTypes]);

    const optionsToShow = useMemo(() => {
        switch (ownership) {
            case 'Finansiel Leasing':
                return {
                    duration: true,
                    kmPerYear: true,
                    downPayment: true,
                    residualValue: true,
                    additionalOptions: false,
                };
            case 'Operationel Leasing':
                return {
                    duration: true,
                    kmPerYear: true,
                    downPayment: true,
                    residualValue: false,
                    additionalOptions: false,
                };
            case 'HiRE Selected':
                return {
                    duration: true,
                    kmPerYear: true,
                    downPayment: false,
                    residualValue: false,
                    additionalOptions: true,
                };
        }
    }, [ownership]);

    const mappedDuration = useMemo(
        () => ({
            value: type?.durationMonths,
            displayValue: `${type?.durationMonths}`,
        }),
        [type?.durationMonths]
    );
    const mappedDurationOptions = useMemo(
        () =>
            type?.durationMonthsOptions
                ? type?.durationMonthsOptions.map((option) => {
                      return {
                          value: option,
                          displayValue: `${option}`,
                      };
                  })
                : [mappedDuration],
        [type?.durationMonthsOptions, mappedDuration]
    );
    const mappedKmPerYear = useMemo(
        () => ({
            value: type?.kilometersPerYear,
            displayValue: `${formatDistance(type?.kilometersPerYear || 0)} ${Abbreviations.KM_SLASH_AAR}`,
        }),
        [type?.kilometersPerYear]
    );
    const mappedKmPerYearOptions = useMemo(
        () =>
            type?.kilometersPerYearOptions
                ? type?.kilometersPerYearOptions.map((option) => {
                      return {
                          value: option,
                          displayValue: `${formatDistance(option)}`,
                      };
                  })
                : [mappedKmPerYear],
        [type?.kilometersPerYearOptions, mappedKmPerYear]
    );

    const mappedResidualValue = useMemo(
        () =>
            !optionsToShow?.residualValue
                ? null
                : {
                      value: (type as VehiclePurchaseType['financialLeasing']).residualValue,
                      displayValue: `${(type as VehiclePurchaseType['financialLeasing']).residualValue} ${Abbreviations.KM_SLASH_AAR}`,
                  },
        [optionsToShow, type]
    );

    const minimumResidualValue = (type as VehiclePurchaseType['financialLeasing'])?.minimumResidualValue;
    const maximumResidualValue = (type as VehiclePurchaseType['financialLeasing'])?.maximumResidualValue;

    const mappedResidualValueOptions = useMemo(() => {
        const options = createLeasemanagerOptions(
            {
                value: defaultLeaseManagerSetupValues?.residualValue ?? 0,
                displayValue: `${formatPrice(defaultLeaseManagerSetupValues?.residualValue || 0)} ${Abbreviations.KR}`,
            },
            minimumResidualValue,
            maximumResidualValue,
            residualValuePlusMinusCount,
            residualValuePercentage,
            Abbreviations.KR,
            true
        );
        return options;
    }, [minimumResidualValue, maximumResidualValue, defaultLeaseManagerSetupValues]);

    const mappedDownPayment = useMemo(
        () => ({
            value: type?.downPayment,
            displayValue: `${type?.downPayment} ${Abbreviations.KR}`,
        }),
        [type?.downPayment]
    );

    const mappedDownPaymentOptions = useMemo(() => {
        const options = createLeasemanagerOptions(
            {
                value: defaultLeaseManagerSetupValues?.downPayment ?? 0,
                displayValue: `${formatPrice(defaultLeaseManagerSetupValues?.downPayment || 0)} ${Abbreviations.KR}`,
            },
            type?.minimumDownPayment,
            type?.maximumDownPayment,
            downPaymentPlusMinusCount,
            downPaymentPercentage,
            Abbreviations.KR,
            true
        );
        return options;
    }, [type?.minimumDownPayment, type?.maximumDownPayment, defaultLeaseManagerSetupValues]);

    const { leaseManagerError: error } = usePriceByOwnership(page);

    const filteredOptionalAddOns = useMemo(() => {
        if (ownership === 'HiRE Selected' && optionalAddOns && optionalAddOns['HiRE Selected']) return optionalAddOns?.['HiRE Selected'] ?? [];
        return [];
    }, [ownership, optionalAddOns]);

    return (
        <CalculationsContainer>
            {error === 'Data Fetch Failed' ? (
                <ErrorText>Vi kan desværre ikke beregne finansieringspriser i øjeblikket. Prøv venligst igen senere.</ErrorText>
            ) : null}

            <CalculatorSection>
                {optionsToShow?.duration && (
                    <SliderContent>
                        <LabelAndValue>
                            <Label>Løbetid</Label>
                            <Label bold>{`${mappedDuration.displayValue} mdr.`}</Label>
                        </LabelAndValue>
                        {mappedDurationOptions && mappedDuration && (
                            <SteppedOptionsSlider
                                options={mappedDurationOptions}
                                defaultValue={mappedDuration}
                                value={mappedDuration}
                                onValueChange={(e) => {
                                    setLeaseManagerFinancialDurationMonths({
                                        durationMonths: e.value,
                                    });
                                }}
                            />
                        )}
                    </SliderContent>
                )}
                {optionsToShow?.kmPerYear && (
                    <SliderContent>
                        <LabelAndValue>
                            <Label>Kilometer</Label>
                            <Label bold>{`${mappedKmPerYear.displayValue}`}</Label>
                        </LabelAndValue>
                        <SteppedOptionsSlider
                            options={mappedKmPerYearOptions}
                            defaultValue={mappedKmPerYear}
                            value={mappedKmPerYear}
                            onValueChange={(e) => {
                                setLeaseManagerFinancialKilometersPerYear({
                                    kilometersPerYear: e.value,
                                });
                            }}
                        />
                    </SliderContent>
                )}

                {optionsToShow?.downPayment && (
                    <SliderContent>
                        <LabelAndValue>
                            <Label>Udbetaling</Label>
                            <Label bold>{formatPrice(mappedDownPayment.value)} kr.</Label>
                        </LabelAndValue>
                        <SteppedOptionsSlider
                            options={mappedDownPaymentOptions}
                            defaultValue={mappedDownPayment}
                            value={mappedDownPayment}
                            onValueChange={(e) => {
                                setLeaseManagerFinancialDownPayment({
                                    downPayment: e.value,
                                });
                            }}
                        />
                    </SliderContent>
                )}

                {optionsToShow?.residualValue && mappedResidualValue && !!mappedDurationOptions.length && (
                    <SliderContent>
                        <LabelAndValue>
                            <Label>Restværdi</Label>
                            <Label bold>{formatPrice((type as VehiclePurchaseType['financialLeasing']).residualValue)} kr.</Label>
                        </LabelAndValue>
                        <SteppedOptionsSlider
                            options={mappedResidualValueOptions}
                            defaultValue={mappedResidualValue}
                            value={mappedResidualValue}
                            onValueChange={(e) => {
                                setLeaseManagerFinancialResidualValue({
                                    residualValue: e.value,
                                });
                            }}
                        />
                    </SliderContent>
                )}
            </CalculatorSection>

            {filteredOptionalAddOns.length > 0 ? (
                <StyledAddOnsSection>
                    <StyledAddOnsHeader>DE GODE TILVALG</StyledAddOnsHeader>
                    <StyledAddOnsOptionsGrid>
                        {filteredOptionalAddOns.map((x) => {
                            return (
                                <StyledAddOnOption key={x.id}>
                                    <StyledCheckbox
                                        checked={x.isSelected ?? false}
                                        id={x.id}
                                        onChange={(isSelected) => updateOptionalEquipment({ id: x.id, isSelected })}
                                        value={x.name}
                                    >
                                        {x.name}
                                    </StyledCheckbox>
                                    <StyledAddOnOptionLink>
                                        {x.description && x.description.length > 0 && (
                                            <Button variant="link" onClick={() => setSelectedOptionalEquipment(x)}>
                                                Læs mere
                                            </Button>
                                        )}
                                    </StyledAddOnOptionLink>
                                    <StyledAddOnOptionPrice>
                                        <span>
                                            {formatPrice(x.monthlyHireSelectedPrice ?? 0)} {Abbreviations.KR_SLASH_MD}
                                        </span>
                                    </StyledAddOnOptionPrice>
                                </StyledAddOnOption>
                            );
                        })}
                    </StyledAddOnsOptionsGrid>
                </StyledAddOnsSection>
            ) : null}
        </CalculationsContainer>
    );
};
